import { ClientPrintJob, DefaultPrinter, FileSourceType, InstalledPrinter, JSPrintManager, PrintFilePDF, WSStatus } from "jsprintmanager7";
import mainConfig from "../common/config/main.config";
import { PrintOptions, PrintServerOptions, ServerConfig } from "./types";
import PrintSettingsCache from "../components/printSettings/cache/PrintSettingsCache";
import GlobalErrorHandler from "../common/utils/errorHandlers/GlobalErrorHandler";

const errorHandler = GlobalErrorHandler.getInstance();

//Check JSPM WebSocket status
function jspmWSStatus() {
  try {
    if (JSPrintManager.websocket_status === WSStatus.Open) return true;
    else if (JSPrintManager.websocket_status === WSStatus.Closed) {
      console.log(
        "JSPrintManager (JSPM) is not installed or not running! Download JSPM Client App from https://neodynamic.com/downloads/jspm"
      );
      return false;
    } else if (JSPrintManager.websocket_status === WSStatus.Blocked) {
      console.log("JSPM has blocked this website!");
      return false;
    }
    return false;
  } catch (e) {
    return false;
  }
}

function isPrintServerConnected() {
  return JSPrintManager.websocket_status === WSStatus.Open;
}

function getPrinter(printerSelected = "", paperName = "") {
  const cpj = new ClientPrintJob();
  if (printerSelected) {
    if(paperName) {
      cpj.clientPrinter = new InstalledPrinter(printerSelected, false, '', paperName);
    } else {
      cpj.clientPrinter = new InstalledPrinter(printerSelected);
    }
  } else {
    cpj.clientPrinter = new DefaultPrinter();
  }
  return cpj;
}

function print(blob: any, options: PrintOptions = {}) {
  try {
    if (jspmWSStatus()) {
      const cpj = getPrinter(options.selectedPrinter, options.paperName);
      const myPdfFile = new PrintFilePDF(blob, FileSourceType.BLOB, "myFileToPrint.pdf", 1);
      cpj.files.push(myPdfFile);
      cpj.sendToClient();
      return;
    }
    throw Error();
  } catch (e) {
    stop();
    const error = new Error("Print operation failed");
    errorHandler.handlePrintServerError(error, 'print');
    throw error;
  }
}

function printZPL(zplStr: string, options: PrintOptions) {
  try {
    if (jspmWSStatus()) {
      const cpj = getPrinter(options.selectedPrinter);
      cpj.printerCommands = zplStr;
      cpj.sendToClient();
      return;
    }
    throw Error();
  } catch (e) {
    stop();
    const error = new Error("ZPL print operation failed");
    errorHandler.handlePrintServerError(error, 'printZPL');
    throw error;
  }
}

const getCachedConnectionValues = () => {
  const cached = PrintSettingsCache.get().settingsValues;
  return { serverIP: cached.serverIP, serverPort: +cached.serverPort || undefined };
};

const getServerConfig = (options?: Partial<PrintServerOptions>) => {
  let serverIP: ServerConfig = undefined;
  let serverPort: ServerConfig = undefined;

  if (options?.useCache) {
    const { serverIP: ip, serverPort: port } = getCachedConnectionValues();
    if (ip) {
      serverIP = ip;
    }
    if (port) {
      serverPort = port;
    }
  } else if (options?.ip) {
    serverIP = options.ip;
    serverPort = options.port;
  }
  return { ip: serverIP, port: serverPort };
};

const setupPdfWS = async (options?: Partial<PrintServerOptions>) => {
  const { ip } = getServerConfig(options);

  try {
    stop();

    const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT)?.registrationApiUrl;
    JSPrintManager.license_url = `${baseApiUrl}/v1/PrintServer/licensev7`;
    JSPrintManager.auto_reconnect = true;
    
    // Wrap JSPrintManager.start() to catch any Event objects it might reject with
    try {
      await JSPrintManager.start(true, ip, 27508);
    } catch (startError) {
      // Convert any Event objects or other non-Error rejections to proper Error
      const error = startError instanceof Error 
        ? startError 
        : new Error(`JSPrintManager.start V7 failed: ${(startError as any)?.type || 'unknown'}`);
      throw error;
    }
    
    return Promise.resolve();
  } catch (e) {
    console.log(e);
    JSPrintManager.auto_reconnect = false;
    stop();
    const error = e instanceof Error ? e : new Error("Unable to connect to Print Server.");
    errorHandler.handlePrintServerError(error, 'setupPdfWS');
    return Promise.reject(error);
  }
};

const stop = () => {
  try {
    JSPrintManager.auto_reconnect = false;
    if (JSPrintManager.websocket_status !== WSStatus.Closed) {
      JSPrintManager.stop();
    }
  } catch (e) {
    console.log('>>>', e);
  }
};

const getAvailablePrinters = async () => {
  try {
    return await JSPrintManager.getPrinters();
  } catch (e) {
    return [];
  }
};

const PrintServiceV7 = {
  stop,
  print,
  printZPL,
  setupPdfWS,
  getAvailablePrinters,
  isPrintServerConnected
};

export default PrintServiceV7;
